import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

/**
 * CreateDealModal — Reusable deal creation modal
 * 
 * Props:
 *   isOpen       — boolean, controls visibility
 *   onClose      — function, called when modal is dismissed
 *   onCreated    — function(deal), called after successful creation
 *   prefill      — object { contact_id, contact_name, account, vertical, campaign_name, source }
 *   navigateOnCreate — boolean, if true navigates to the new deal page (default: false)
 */
const CreateDealModal = ({ isOpen, onClose, onCreated, prefill = {}, navigateOnCreate = false }) => {
  const navigate = useNavigate();
  
  // Form state
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [deal, setDeal] = useState({
    name: '',
    account: '',
    customer_type: 'brand',
    brand_name: '',
    reseller_name: '',
    stage: 'prospecting',
    vertical: 'other',
    campaign_start_date: '',
    campaign_end_date: '',
    expected_close_date: '',
    total_deal_value: '',
    cost_breakdown: {},
    products_utilized: [],
    seller_id: '',
    notes: '',
    contact_ids: [],
    deal_source: 'direct',
    partner_id: '',
    partner_rev_share: '',
    partner_seller_id: ''
  });

  // Reference data
  const [sellers, setSellers] = useState([]);
  const [partnerSellers, setPartnerSellers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [talentLists, setTalentLists] = useState([]);
  const [fixedCosts, setFixedCosts] = useState(['Creative Production', 'Misc Fees', 'Travel Fees']);
  const [stages, setStages] = useState({});
  const [showNewPartner, setShowNewPartner] = useState(false);
  const [newPartner, setNewPartner] = useState({ name: '', rev_share_percent: '', notes: '' });

  // Tier state for multi-tier deals
  const [tiersEnabled, setTiersEnabled] = useState(false);
  const [tierValues, setTierValues] = useState(['', '', '']); // up to 3 tiers
  const [tierCostBreakdowns, setTierCostBreakdowns] = useState([{}, {}, {}]); // per-tier cost breakdown
  const [tierCount, setTierCount] = useState(2);
  const [activeTierIdx, setActiveTierIdx] = useState(0);

  const verticals = [
    { id: 'gaming', label: 'Gaming' }, { id: 'entertainment', label: 'Entertainment' },
    { id: 'cpg', label: 'CPG' }, { id: 'foodbev', label: 'Food & Bev' },
    { id: 'qsr', label: 'QSR' }, { id: 'insurance', label: 'Insurance' },
    { id: 'finance', label: 'Finance' }, { id: 'auto', label: 'Auto' },
    { id: 'retail', label: 'Retail' }, { id: 'sports', label: 'Sports' },
    { id: 'tech', label: 'Tech' }, { id: 'telco', label: 'Telco' },
    { id: 'other', label: 'Other' }
  ];

  // Fetch reference data when modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchData = async () => {
      try {
        const [pipelineRes, sellersRes, contactsRes, talentListsRes] = await Promise.all([
          api.get('/pipeline'),
          api.get('/sellers'),
          api.get('/contacts'),
          api.get('/talent-lists').catch(() => ({ data: { lists: [] } }))
        ]);
        setStages(pipelineRes.data.stages || {});
        setProducts(pipelineRes.data.products || []);
        if (pipelineRes.data.fixedCosts) setFixedCosts(pipelineRes.data.fixedCosts);
        if (pipelineRes.data.partners) setPartners(pipelineRes.data.partners);
        setSellers(sellersRes.data.sellers || []);
        setContacts(contactsRes.data.contacts || []);
        setTalentLists(talentListsRes.data.lists || []);
      } catch (e) {
        console.error('Failed to fetch deal form data:', e);
      }
    };
    fetchData();
  }, [isOpen]);

  // Fetch partner sellers when partner changes
  useEffect(() => {
    if (deal.deal_source === 'partner' && deal.partner_id) {
      api.get(`/sales-partners/${deal.partner_id}/sellers`).then(r => {
        setPartnerSellers(r.data.sellers || []);
      }).catch(() => setPartnerSellers([]));
    } else {
      setPartnerSellers([]);
    }
  }, [deal.partner_id, deal.deal_source]);

  // Apply prefill when modal opens or prefill changes
  useEffect(() => {
    if (!isOpen) return;
    
    const sourceLabel = prefill.source === 'reply' ? 'Reply' :
                        prefill.source === 'hot_lead' ? 'Hot Lead' :
                        prefill.source === 'contact' ? 'Contact' :
                        prefill.source === 'company' ? 'Company' : 'Outreach';
    
    setDeal(prev => ({
      ...prev,
      name: prefill.account ? `${prefill.account} — ${sourceLabel}` : '',
      account: prefill.account || '',
      vertical: prefill.vertical || 'other',
      contact_ids: prefill.contact_id ? [prefill.contact_id] : [],
      notes: prefill.campaign_name ? `Source: ${prefill.campaign_name}` : ''
    }));
    setStep(1);
  }, [isOpen, prefill.contact_id, prefill.account, prefill.vertical, prefill.campaign_name, prefill.source]);

  // Margin calculation — uses active tier value when tiers enabled
  const calcMargins = (overrideValue) => {
    const rawValue = overrideValue != null ? overrideValue
      : tiersEnabled ? (tierValues[activeTierIdx] || 0)
      : deal.total_deal_value;
    const dealValue = parseFloat(rawValue) || 0;
    const isPartner = deal.deal_source === 'partner' && deal.partner_rev_share;
    const revSharePct = parseFloat(deal.partner_rev_share) || 0;
    const partnerNetValue = isPartner ? dealValue * (revSharePct / 100) : dealValue;
    let totalHardCosts = 0;
    const activeCosts = tiersEnabled ? (tierCostBreakdowns[activeTierIdx] || {}) : deal.cost_breakdown;
    Object.values(activeCosts).forEach(c => { totalHardCosts += parseFloat(c) || 0; });
    const commission = isPartner ? 0 : dealValue * 0.10;
    const netRevenue = partnerNetValue - totalHardCosts - commission;
    const grossMarginPercent = partnerNetValue > 0 ? ((netRevenue / partnerNetValue) * 100).toFixed(2) : '0.00';
    return { dealValue, partnerNetValue, isPartner, revSharePct, totalHardCosts, commission, netRevenue, grossMarginPercent };
  };

  const formatTierLabel = (idx) => {
    const val = parseFloat(tierValues[idx]) || 0;
    const fmt = val >= 1e6 ? `$${(val / 1e6).toFixed(val % 1e6 === 0 ? 0 : 2)}M`
      : val >= 1e3 ? `$${Math.round(val / 1e3)}K`
      : val > 0 ? formatCurrency(val) : '';
    return `Tier ${idx + 1}${fmt ? ` | ${fmt}` : ''}`;
  };

  const margins = calcMargins();
  const formatCurrency = (n) => '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  // Create new partner inline
  const handleCreatePartner = async () => {
    if (!newPartner.name || !newPartner.rev_share_percent) return;
    try {
      const res = await api.post('/sales-partners', newPartner);
      const created = res.data.partner;
      setPartners(prev => [...prev, created]);
      setDeal(prev => ({ ...prev, partner_id: created.id, partner_rev_share: created.rev_share_percent }));
      setShowNewPartner(false);
      setNewPartner({ name: '', rev_share_percent: '', notes: '' });
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to create partner');
    }
  };

  const toggleProduct = (product) => {
    setDeal(prev => {
      const isSelected = prev.products_utilized.includes(product);
      const newProducts = isSelected ? prev.products_utilized.filter(p => p !== product) : [...prev.products_utilized, product];
      const newCosts = { ...prev.cost_breakdown };
      if (isSelected) delete newCosts[product];
      return { ...prev, products_utilized: newProducts, cost_breakdown: newCosts };
    });
  };

  const toggleContact = (id) => {
    setDeal(prev => ({
      ...prev,
      contact_ids: prev.contact_ids.includes(id) ? prev.contact_ids.filter(c => c !== id) : [...prev.contact_ids, id]
    }));
  };

  const updateCost = (key, value) => {
    if (tiersEnabled) {
      setTierCostBreakdowns(prev => {
        const next = [...prev];
        next[activeTierIdx] = { ...next[activeTierIdx], [key]: value };
        return next;
      });
    } else {
      setDeal(prev => ({ ...prev, cost_breakdown: { ...prev.cost_breakdown, [key]: value } }));
    }
  };

  const handleCreate = async () => {
    if (!deal.name) { alert('Deal name is required'); return; }
    if (tiersEnabled && !tierValues[0]) { alert('At least Tier 1 deal value is required'); return; }
    if (!tiersEnabled && !deal.total_deal_value) { alert('Deal value is required'); return; }

    setCreating(true);
    try {
      // For multi-tier deals, use Tier 1 value as the headline deal value
      const submitDeal = {
        ...deal,
        tiers_enabled: tiersEnabled,
        total_deal_value: tiersEnabled ? (parseFloat(tierValues[0]) || 0) : deal.total_deal_value,
      };

      const res = await api.post('/deals', submitDeal);
      const newDeal = res.data.deal;

      // Create tier records for multi-tier deals
      if (tiersEnabled) {
        for (let i = 0; i < tierCount; i++) {
          if (tierValues[i]) {
            await api.post(`/deals/${newDeal.id}/tiers`, {
              total_deal_value: parseFloat(tierValues[i]) || 0,
              cost_breakdown: tierCostBreakdowns[i] || {},
            }).catch(() => {});
          }
        }
      }
      
      // Log outreach source activity
      if (prefill.source) {
        const sourceDesc = prefill.source === 'reply' 
          ? `Deal created from outreach reply${prefill.campaign_name ? ` (Campaign: ${prefill.campaign_name})` : ''}`
          : prefill.source === 'hot_lead'
          ? `Deal created from hot lead${prefill.campaign_name ? ` (Campaign: ${prefill.campaign_name})` : ''}`
          : prefill.source === 'contact'
          ? `Deal created from contact profile${prefill.contact_name ? ` — ${prefill.contact_name}` : ''}`
          : prefill.source === 'company'
          ? `Deal created from company page — ${prefill.account || ''}`
          : `Deal created from outreach`;
        
        await api.post(`/deals/${newDeal.id}/activities`, {
          activity_type: 'outreach_conversion',
          subject: 'Outreach → Deal',
          description: sourceDesc
        }).catch(() => {}); // Non-fatal
      }
      
      onClose();
      if (onCreated) onCreated(newDeal);
      if (navigateOnCreate) navigate(`/pipeline/${newDeal.id}`);

      // Reset
      setStep(1);
      setTiersEnabled(false);
      setTierValues(['', '', '']);
      setTierCostBreakdowns([{}, {}, {}]);
      setTierCount(2);
      setActiveTierIdx(0);    } catch (e) {
      alert(e.response?.data?.error || 'Failed to create deal');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setTiersEnabled(false);
    setTierValues(['', '', '']);
    setTierCostBreakdowns([{}, {}, {}]);
    setTierCount(2);
    setActiveTierIdx(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-8" onClick={handleClose}>
      <div 
        className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 flex justify-between items-start sticky top-0 bg-zinc-900 z-10">
          <div>
            <h2 className="text-xl font-bold text-white">
              {step === 1 ? 'Create Deal — Info' : 'Create Deal — Margin Calculator'}
            </h2>
            <div className="flex gap-2 mt-2">
              <div className={`w-8 h-1 rounded ${step >= 1 ? 'bg-nextide-turquoise' : 'bg-zinc-700'}`} />
              <div className={`w-8 h-1 rounded ${step >= 2 ? 'bg-nextide-turquoise' : 'bg-zinc-700'}`} />
            </div>
            {prefill.source && (
              <div className="mt-2 text-xs text-nextide-turquoise">
                {prefill.source === 'reply' ? '📥 From outreach reply' :
                 prefill.source === 'hot_lead' ? '🔥 From hot lead' :
                 prefill.source === 'contact' ? '👤 From contact' :
                 prefill.source === 'company' ? '🏢 From company' : '✉️ From outreach'}
                {prefill.contact_name && ` — ${prefill.contact_name}`}
              </div>
            )}
          </div>
          <button onClick={handleClose} className="text-zinc-400 hover:text-white text-2xl leading-none">×</button>
        </div>

        {/* Body */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Deal Name *</label>
                  <input type="text" value={deal.name} onChange={e => setDeal({ ...deal, name: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white" placeholder="Q1 Campaign" />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Account</label>
                  <input type="text" value={deal.account} onChange={e => setDeal({ ...deal, account: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white" placeholder="Nike" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">NexTide Customer</label>
                  <select value={deal.customer_type} onChange={e => setDeal({ ...deal, customer_type: e.target.value, brand_name: '', reseller_name: '' })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white">
                    <option value="brand">Brand Direct</option>
                    <option value="agency">Agency</option>
                    <option value="reseller">Reseller</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Stage</label>
                  <select value={deal.stage} onChange={e => setDeal({ ...deal, stage: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white">
                    {Object.entries(stages).length > 0 
                      ? Object.entries(stages).map(([k, v]) => <option key={k} value={k}>{v.label} ({v.percent}%)</option>)
                      : <>
                          <option value="prospecting">Prospecting (5%)</option>
                          <option value="rfp_received">RFP Received (10%)</option>
                          <option value="rfp_submitted">RFP Submitted (20%)</option>
                          <option value="client_recommended">Client Recommended (40%)</option>
                          <option value="negotiation">Negotiation (60%)</option>
                          <option value="verbal">Verbal (75%)</option>
                          <option value="io_sent">IO Sent (90%)</option>
                          <option value="closed_won">Closed Won (100%)</option>
                        </>
                    }
                  </select>
                </div>
              </div>

              {deal.customer_type === 'agency' && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Which Agency?</label>
                  <input type="text" value={deal.brand_name} onChange={e => setDeal({ ...deal, brand_name: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white" placeholder="Enter agency name" />
                </div>
              )}
              {deal.customer_type === 'reseller' && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Which Reseller?</label>
                  <input type="text" value={deal.reseller_name} onChange={e => setDeal({ ...deal, reseller_name: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white" placeholder="Enter reseller name" />
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Campaign Start</label>
                  <input type="date" value={deal.campaign_start_date} onChange={e => setDeal({ ...deal, campaign_start_date: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Campaign End</label>
                  <input type="date" value={deal.campaign_end_date} onChange={e => setDeal({ ...deal, campaign_end_date: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Expected Close</label>
                  <input type="date" value={deal.expected_close_date} onChange={e => setDeal({ ...deal, expected_close_date: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Vertical</label>
                  <select value={deal.vertical} onChange={e => setDeal({ ...deal, vertical: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white">
                    {verticals.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Seller</label>
                  <select value={deal.deal_source === 'partner' ? (deal.partner_seller_id || '') : (deal.seller_id || '')}
                    onChange={e => {
                      if (deal.deal_source === 'partner') setDeal({ ...deal, partner_seller_id: e.target.value });
                      else setDeal({ ...deal, seller_id: e.target.value });
                    }}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white">
                    <option value="">Select...</option>
                    {(deal.deal_source === 'partner' && deal.partner_id ? partnerSellers : sellers).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Deal Source: Direct vs Partner */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Deal Source</label>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setDeal({ ...deal, deal_source: 'direct', partner_id: '', partner_rev_share: '', seller_id: '', partner_seller_id: '' })}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${deal.deal_source === 'direct' ? 'bg-nextide-turquoise text-black border-nextide-turquoise' : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-500'}`}>
                    Direct (100%)
                  </button>
                  <button type="button" onClick={() => setDeal({ ...deal, deal_source: 'partner' })}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${deal.deal_source === 'partner' ? 'bg-purple-600 text-white border-purple-500' : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-500'}`}>
                    Sales Partner
                  </button>
                </div>
              </div>

              {deal.deal_source === 'partner' && (
                <div className="bg-purple-900/20 border border-purple-800/30 rounded-xl p-4 space-y-3">
                  {!showNewPartner ? (
                    <>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <label className="block text-sm text-zinc-400 mb-1">Select Partner</label>
                          <select value={deal.partner_id} onChange={e => {
                            const p = partners.find(p => p.id === parseInt(e.target.value));
                            setDeal({ ...deal, partner_id: e.target.value, partner_rev_share: p ? p.rev_share_percent : '', seller_id: '', partner_seller_id: '' });
                          }} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white">
                            <option value="">Select partner...</option>
                            {partners.map(p => <option key={p.id} value={p.id}>{p.name} ({p.rev_share_percent}% NexTide)</option>)}
                          </select>
                        </div>
                        <button type="button" onClick={() => setShowNewPartner(true)}
                          className="mt-5 px-3 py-2 bg-purple-700 text-white rounded-lg text-sm font-semibold hover:bg-purple-600 whitespace-nowrap">
                          + New Partner
                        </button>
                      </div>
                      {deal.partner_rev_share && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-zinc-400">NexTide Rev Share:</span>
                          <span className="text-purple-300 font-bold">{deal.partner_rev_share}%</span>
                          <span className="text-zinc-500">|</span>
                          <span className="text-zinc-400">Partner keeps:</span>
                          <span className="text-zinc-300 font-bold">{(100 - parseFloat(deal.partner_rev_share)).toFixed(0)}%</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-3">
                      <div className="text-sm font-semibold text-purple-300">New Sales Partner</div>
                      <div className="grid grid-cols-2 gap-3">
                        <input type="text" placeholder="Partner name" value={newPartner.name}
                          onChange={e => setNewPartner({ ...newPartner, name: e.target.value })}
                          className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                        <div className="relative">
                          <input type="number" placeholder="NexTide %" value={newPartner.rev_share_percent}
                            onChange={e => setNewPartner({ ...newPartner, rev_share_percent: e.target.value })}
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">%</span>
                        </div>
                      </div>
                      <input type="text" placeholder="Notes (optional)" value={newPartner.notes}
                        onChange={e => setNewPartner({ ...newPartner, notes: e.target.value })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setShowNewPartner(false)}
                          className="px-3 py-1.5 bg-zinc-700 text-white rounded-lg text-sm">Cancel</button>
                        <button type="button" onClick={handleCreatePartner}
                          disabled={!newPartner.name || !newPartner.rev_share_percent}
                          className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50">Save Partner</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {products.length > 0 && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Products</label>
                  <div className="flex flex-wrap gap-2">
                    {products.map(p => (
                      <button key={p} type="button" onClick={() => toggleProduct(p)}
                        className={`px-3 py-1 rounded-lg text-sm ${deal.products_utilized.includes(p) ? 'bg-nextide-turquoise text-black' : 'bg-zinc-800 text-zinc-400'}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm text-zinc-400 mb-1">
                  Contacts ({deal.contact_ids.length})
                  {prefill.contact_name && <span className="text-nextide-turquoise ml-1">— {prefill.contact_name} pre-selected</span>}
                </label>
                <div className="bg-zinc-800 border border-zinc-700 rounded-lg max-h-32 overflow-y-auto">
                  {contacts.length === 0 ? (
                    <div className="p-3 text-zinc-500 text-sm">Loading contacts...</div>
                  ) : contacts.map(c => (
                    <label key={c.id} className="flex items-center p-2 hover:bg-zinc-700 cursor-pointer">
                      <input type="checkbox" checked={deal.contact_ids.includes(c.id)} onChange={() => toggleContact(c.id)} className="mr-2" />
                      <span className="text-white text-sm">{c.name || c.email}</span>
                      {c.company && <span className="text-zinc-500 text-xs ml-2">({c.company})</span>}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Notes</label>
                <textarea value={deal.notes} onChange={e => setDeal({ ...deal, notes: e.target.value })} rows={2}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white" />
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button type="button" onClick={() => { setTiersEnabled(!tiersEnabled); setActiveTierIdx(0); }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${tiersEnabled ? 'bg-nextide-turquoise' : 'bg-zinc-700'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${tiersEnabled ? 'translate-x-6' : 'translate-x-1'}`}/>
                </button>
                <span className="text-sm text-zinc-400">Multiple Tiers</span>
                {tiersEnabled && <span className="text-xs text-zinc-500">Each tier gets its own deal value & margin</span>}
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Link Talent List (optional)</label>
                <select value={deal.talent_list_id || ''} onChange={e => setDeal({ ...deal, talent_list_id: e.target.value || null })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white">
                  <option value="">None</option>
                  {talentLists.filter(l => !l.deal_id).map(l => (
                    <option key={l.id} value={l.id}>{l.name} ({l.item_count || 0} creators)</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-4 pt-4">
                <button onClick={handleClose} className="flex-1 bg-zinc-800 text-white px-6 py-3 rounded-lg font-semibold">Cancel</button>
                <button onClick={() => { if (!deal.name) { alert('Enter a deal name'); return; } setStep(2); }}
                  className="flex-1 bg-nextide-turquoise text-black px-6 py-3 rounded-lg font-semibold">Next →</button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">

              {tiersEnabled ? (
                <div>
                  {/* Tier tabs + add button */}
                  <div className="flex items-center gap-2 mb-4">
                    {Array.from({ length: tierCount }).map((_, idx) => (
                      <button key={idx} type="button" onClick={() => setActiveTierIdx(idx)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium ${activeTierIdx === idx ? 'bg-nextide-turquoise text-black' : 'bg-zinc-800 text-zinc-300'}`}>
                        {formatTierLabel(idx)}
                      </button>
                    ))}
                    {tierCount < 3 && (
                      <button type="button" onClick={() => { setTierCount(tierCount + 1); setActiveTierIdx(tierCount); }}
                        className="px-3 py-1.5 rounded-lg text-xs bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700 border-dashed">
                        + Tier {tierCount + 1}
                      </button>
                    )}
                  </div>

                  {/* Active tier value */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Tier {activeTierIdx + 1} Deal Value *</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                      <input type="number" value={tierValues[activeTierIdx]} placeholder="50000"
                        onChange={e => { const v = [...tierValues]; v[activeTierIdx] = e.target.value; setTierValues(v); }}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-8 pr-4 py-3 text-white text-xl font-bold" />
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Total Deal Value (what client pays) *</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                    <input type="number" value={deal.total_deal_value} onChange={e => setDeal({ ...deal, total_deal_value: e.target.value })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-8 pr-4 py-3 text-white text-xl font-bold" placeholder="50000" />
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Cost Breakdown {tiersEnabled && <span className="text-sm font-normal text-zinc-500">(Tier {activeTierIdx + 1})</span>}</h3>
                {deal.products_utilized.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm text-zinc-500 mb-2">Product Costs</div>
                    <div className="grid grid-cols-2 gap-3">
                      {deal.products_utilized.map(p => (
                        <div key={p} className="flex items-center gap-2">
                          <label className="text-zinc-300 text-sm w-36 truncate">{p}</label>
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                            <input type="number" value={(tiersEnabled ? tierCostBreakdowns[activeTierIdx] : deal.cost_breakdown)[p] || ''} onChange={e => updateCost(p, e.target.value)}
                              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-7 pr-3 py-2 text-white text-sm" placeholder="0" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-sm text-zinc-500 mb-2">Additional Costs</div>
                  <div className="grid grid-cols-2 gap-3">
                    {fixedCosts.map(c => (
                      <div key={c} className="flex items-center gap-2">
                        <label className="text-zinc-300 text-sm w-36">{c}</label>
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                          <input type="number" value={(tiersEnabled ? tierCostBreakdowns[activeTierIdx] : deal.cost_breakdown)[c] || ''} onChange={e => updateCost(c, e.target.value)}
                            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-7 pr-3 py-2 text-white text-sm" placeholder="0" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Summary — shows for active tier when multi-tier */}
              {(() => {
                const m = calcMargins();
                return (
                  <div className="bg-zinc-800 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">
                      {tiersEnabled ? `Tier ${activeTierIdx + 1} Summary` : 'Deal Summary'}
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400">Total Deal Value</span>
                        <span className="text-white font-medium">{formatCurrency(m.dealValue)}</span>
                      </div>
                      {m.isPartner && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-purple-400">Partner Rev Share ({m.revSharePct}% NexTide)</span>
                            <span className="text-purple-300 font-medium">- {formatCurrency(m.dealValue - m.partnerNetValue)}</span>
                          </div>
                          <div className="flex justify-between text-sm border-t border-zinc-700 pt-2">
                            <span className="text-purple-300 font-semibold">Net to NexTide</span>
                            <span className="text-purple-300 font-bold">{formatCurrency(m.partnerNetValue)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-400">Total Hard Costs</span>
                        <span className="text-red-400 font-medium">- {formatCurrency(m.totalHardCosts)}</span>
                      </div>
                      {!m.isPartner ? (
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400">Commission (10%)</span>
                          <span className="text-red-400 font-medium">- {formatCurrency(m.commission)}</span>
                        </div>
                      ) : (
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-500 italic">Commission</span>
                          <span className="text-zinc-600 italic">N/A (partner deal)</span>
                        </div>
                      )}
                      <hr className="border-zinc-700" />
                      <div className="flex justify-between">
                        <span className="text-white font-semibold">Net Revenue</span>
                        <span className={`text-xl font-bold ${m.netRevenue >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(m.netRevenue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white font-semibold">Gross Margin</span>
                        <span className={`text-xl font-bold ${parseFloat(m.grossMarginPercent) >= 0 ? 'text-green-400' : 'text-red-400'}`}>{m.grossMarginPercent}%</span>
                      </div>
                      {tiersEnabled && tierCount > 1 && (
                        <div className="pt-2 border-t border-zinc-700 flex gap-4">
                          {Array.from({ length: tierCount }).map((_, idx) => idx !== activeTierIdx && (
                            <div key={idx} className="text-xs text-zinc-500">
                              Tier {idx + 1}: {tierValues[idx] ? formatCurrency(parseFloat(tierValues[idx])) : '—'}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div className="flex gap-4 pt-4">
                <button onClick={() => setStep(1)} className="flex-1 bg-zinc-800 text-white px-6 py-3 rounded-lg font-semibold">← Back</button>
                <button onClick={handleCreate}
                  disabled={creating || (tiersEnabled ? !tierValues[0] : !deal.total_deal_value)}
                  className="flex-1 bg-nextide-turquoise text-black px-6 py-3 rounded-lg font-semibold disabled:opacity-50">
                  {creating ? 'Creating...' : 'Create Deal'}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default CreateDealModal;
